import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "es-toolkit";
import { selectOrderByUuid } from "./reducers/selectors.tsx";
import EditableTextArea from "../../react/EditableTextArea";
import { translate } from "../../IntlProviderWrapper";
import * as Auth from "../../../lib/Auth";
import { useUpdateOrderComment } from "../../../lib/custom-hooks/queryComment";
import { onSaveComment } from "./PlanningTableEditModal";
import type { ReduxState, ActionTypes } from "./reducers/reducers.tsx";
import type { IOrderProducedModelEx } from "./reducers/linesOrders.tsx";

const SAVE_DEBOUNCE_TIME_MS = 1000;

type Props = {
    order_uuid: string;
    comment: string;
};

const OrderInlineComment = (props: Props) => {
    const { order_uuid, comment } = props;

    const [inputComment, setInputComment] = useState(comment);
    const inputCommentRef = useRef(inputComment);
    inputCommentRef.current = inputComment;

    const order = useSelector((state: ReduxState) => selectOrderByUuid(state, order_uuid));
    const reduxDispatch = useDispatch<Dispatch<ActionTypes>>();
    const updateOrderComment = useUpdateOrderComment();

    /** A single debounced function instance that is stable and not recreated on every render */
    const debouncedSave = useMemo(
        () =>
            debounce((order: IOrderProducedModelEx, newComment: string) => {
                updateOrderComment.mutate(
                    { order, comment: newComment },
                    {
                        onSuccess: () => {
                            if (newComment !== inputCommentRef.current) {
                                return;
                            }
                            onSaveComment(order.uuid, newComment, reduxDispatch);
                        },
                    }
                );
            }, SAVE_DEBOUNCE_TIME_MS),
        // Dependencies are not required here — maintain a single instance of the function
        // mutate and dispatch are stable; inputCommentRef is read at success time.
        []
    );

    useEffect(() => {
        setInputComment(comment);
        inputCommentRef.current = comment;
    }, [comment]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [order_uuid, debouncedSave]);

    const handleChange = useCallback(
        (value: string) => {
            setInputComment(value);
            inputCommentRef.current = value;
            if (!order) {
                return;
            }
            if (value !== comment) {
                debouncedSave(order, value);
            } else {
                debouncedSave.cancel();
            }
        },
        [order, comment, debouncedSave]
    );

    const isDisabled = !Auth.hasPermission(Auth.PERMISSION_NAMES.OrderEdit);

    return (
        <EditableTextArea
            value={inputComment}
            onChange={handleChange}
            isDisabled={isDisabled}
            placeholder={translate("common.add_comment", "Add comment")}
            title={translate("common.comment", "Comment")}
            size="small"
        />
    );
};

export default OrderInlineComment;
